import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { isAttestationExpiredError } from '../api/attach-attestation-expiry';
import { usesDevAuthToken } from './auth-config';

const AUTH_RETRY_FLAG = '__authRetried';

export interface ApiAuthHandlers {
  getAccessToken: () => Promise<string | null>;
  tryRecoverSessionSilently: () => Promise<boolean>;
  onUnauthorized: (status: number) => Promise<void>;
}

type RetriableAxiosRequestConfig = InternalAxiosRequestConfig & {
  [AUTH_RETRY_FLAG]?: boolean;
};

/**
 * Attaches Okta bearer tokens to the dedicated API axios instance.
 * On 401/403, attempts one silent token renewal and retries before redirecting to Okta login.
 * Expired-attestation failures are excluded — those belong to the wizard, not to the Okta session.
 */
export function attachApiAuthInterceptors(
  axiosInstance: AxiosInstance,
  handlers: ApiAuthHandlers,
): void {
  axiosInstance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const token = await handlers.getAccessToken();
    if (!token) {
      return config;
    }

    const authorization = usesDevAuthToken() ? token : `Bearer ${token}`;
    config.headers.set('Authorization', authorization);
    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: { response?: { status?: number }; config?: RetriableAxiosRequestConfig }) => {
      const status = error.response?.status;
      const config = error.config;

      // An expired wizard attestation is not an Okta session problem: let the attestation
      // interceptor clear the JWTs and return to Ship From / Ship To instead of re-login.
      if (
        (status === 401 || status === 403) &&
        config &&
        !config[AUTH_RETRY_FLAG] &&
        !isAttestationExpiredError(error)
      ) {
        const recovered = await handlers.tryRecoverSessionSilently();
        if (recovered) {
          config[AUTH_RETRY_FLAG] = true;
          return axiosInstance.request(config);
        }

        await handlers.onUnauthorized(status);
      }

      if (error instanceof Error) {
        throw error;
      }

      throw new Error('API request failed', { cause: error });
    },
  );
}
